import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchOrder } from "@/lib/api";
import { CheckCircle2, Package, ArrowRight } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const Confirmation = () => {
  useDocumentTitle("Commande confirmée — cartonmove", "Votre commande de cartons a bien été enregistrée. Livraison en Île-de-France.");
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);

  useEffect(() => {
    if (!order) {
      fetchOrder(orderId)
        .then(setOrder)
        .catch(() => setOrder(null))
        .finally(() => setLoading(false));
    }
  }, [order, orderId]);

  if (loading) return <div className="container-cdm py-20 text-center">Chargement…</div>;
  if (!order)
    return (
      <div className="container-cdm py-20 text-center" data-testid="confirmation-error">
        <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] uppercase">Commande introuvable</h1>
        <Link to="/" className="btn-primary mt-6 inline-flex">Retour à l'accueil</Link>
      </div>
    );

  return (
    <div data-testid="confirmation-page">
      <section className="container-cdm py-16 md:py-20">
        <div className="max-w-2xl mx-auto text-center fade-up">
          <div className="inline-flex h-16 w-16 items-center justify-center bg-[#237cb5]/10 text-[#237cb5] rounded-full mb-6">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <div className="overline">Merci pour votre commande</div>
          <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px]  mt-3" data-testid="confirmation-title">
            Commande confirmée !
          </h1>
          <p className="mt-4 text-[#3a4a5a] leading-relaxed">
            Votre commande a bien été enregistrée. Notre équipe vous contactera très vite au
            <strong> {order.customer.phone}</strong> pour confirmer la livraison à
            <strong> {order.customer.city}</strong>.
          </p>

          <div className="mt-8 bg-white border border-[#e6edf3] p-6 text-left">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Numéro de commande</div>
                <div className="font-display font-extrabold text-lg mt-1" data-testid="confirmation-order-number">{order.order_number}</div>
              </div>
              <Package className="h-8 w-8 text-[#237cb5]" />
            </div>

            <div className="divide-y divide-zinc-100">
              {order.items.map((it) => (
                <div key={it.product_id} className="flex justify-between py-3 text-sm">
                  <span>{it.quantity} × {it.name}</span>
                  <span className="font-semibold">{it.line_total.toFixed(2).replace(".", ",")} €</span>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-[#e6edf3] pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#5b6b7a]">Sous-total</span><span>{order.subtotal.toFixed(2).replace(".", ",")} €</span></div>
              <div className="flex justify-between"><span className="text-[#5b6b7a]">Livraison</span><span>{order.shipping_fee === 0 ? "Offerte" : `${order.shipping_fee.toFixed(2).replace(".", ",")} €`}</span></div>
              <div className="flex justify-between border-t border-[#e6edf3] pt-3 mt-3">
                <span className="font-display font-bold uppercase">Total à régler</span>
                <span className="font-display font-extrabold text-xl">{order.total.toFixed(2).replace(".", ",")} €</span>
              </div>
            </div>

            <div className="mt-4 text-xs text-[#5b6b7a]">
              Paiement à effectuer à la livraison (espèces, CB ou virement).
            </div>
          </div>

          <div className="mt-8">
            <Link to="/" className="btn-outline-dark inline-flex" data-testid="confirmation-home-link">
              Retour à l'accueil <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Confirmation;
